import React from 'react';
import {
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { goBackOrHome } from '../../navigation/goBackOrHome';
import { MaterialIcons } from '@expo/vector-icons';
import { FeedbackForm } from '../../components/FeedbackForm';
import { BrandLogo } from '../../components';
import { fpColors } from '../../styles/theme';

/**
 * Feedback screen shell — Fidelity Purple top bar + welcome kicker +
 * existing FeedbackForm. The form itself still uses legacy tokens
 * (tracked for the component-level token-migration pass) but the
 * surrounding page is on-brand.
 */
export const FeedbackScreen: React.FC = () => {
  const navigation = useNavigation<any>();

  return (
    <View style={styles.page}>
      <View style={styles.topBar}>
        <View style={styles.topBarInner}>
          <Pressable
            onPress={() => goBackOrHome(navigation)}
            hitSlop={8}
            style={styles.topBtn}
            accessibilityRole="button"
            accessibilityLabel="Back"
          >
            <MaterialIcons name="arrow-back" size={22} color={fpColors.onSurface} />
          </Pressable>
          <View style={styles.topBrand}>
            <BrandLogo variant="horizontal" height={22} tone="color" />
          </View>
          <View style={styles.topBtn} />
        </View>
      </View>

      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={styles.scroll}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        <Text style={styles.kicker}>FEEDBACK</Text>
        <Text style={styles.h1}>We\u2019d love to hear from you</Text>
        <Text style={styles.subtitle}>
          Share bugs, ideas, or general thoughts. Every note goes
          directly to the team.
        </Text>

        <View style={styles.card}>
          <FeedbackForm
            onSubmitSuccess={() => goBackOrHome(navigation)}
            onCancel={() => goBackOrHome(navigation)}
            showCancelButton
            showScreenshot={false}
          />
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  page: { flex: 1, backgroundColor: fpColors.background },
  topBar: {
    backgroundColor: fpColors.surfaceContainerLowest,
    borderBottomWidth: 1,
    borderBottomColor: fpColors.outlineVariant,
    paddingTop: Platform.OS === 'ios' ? 48 : 12,
  },
  topBarInner: {
    height: 56,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    gap: 10,
  },
  topBtn: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  topBrand: { flex: 1, alignItems: 'center' },

  scroll: { padding: 20, gap: 14, paddingBottom: 40 },
  kicker: {
    fontFamily: 'Inter_500Medium',
    fontSize: 11,
    color: fpColors.primary,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  h1: {
    fontFamily: 'Inter_800ExtraBold',
    fontSize: 26,
    color: fpColors.onSurface,
    letterSpacing: -0.4,
    marginTop: 2,
  },
  subtitle: {
    fontFamily: 'Inter_400Regular',
    fontSize: 13,
    lineHeight: 18,
    color: fpColors.onSurfaceVariant,
    marginBottom: 6,
  },
  card: {
    padding: 14,
    borderRadius: 16,
    backgroundColor: fpColors.surfaceContainerLowest,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
  },
});
